/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './aeration-settings.scss';

/* eslint-disable import/no-unresolved, import/default */

import aerationSettingsTemplate from './aeration-settings.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.aerationSettings', [])
    .directive('agAerationSettings', AerationSettings)
    .name;

/*@ngInject*/
function AerationSettings() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '='
        },
        controller: AerationSettingsController,
        controllerAs: 'vm',
        templateUrl: aerationSettingsTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function AerationSettingsController($scope, $log, $rootScope, $translate, $q, $filter, $timeout, $mdDialog, agUtils, types, agTypes, assetService, attributeService,
                                    aerationService, userService) {

    let vm = this;

    vm.agTypes = agTypes;

    vm.loading = false;

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.aerationId = null;
    vm.aeration = null;
    vm.origAeration = null;
    vm.allowedTimeFrameFrom = null;
    vm.allowedTimeFrameTo = null;

    vm.save = save;
    vm.cancel = cancel;
    vm.aerationTypeChange = aerationTypeChange;
    vm.isAutoControlSupported = false;

    vm.getIcon = () => {
        if (!vm.aeration) return undefined
        return agUtils.getAssetIcon(vm.aeration.aerationType, vm.aeration.highestAlarmSeverity, vm.aeration.highestAlarmStatus)
    }

    vm.settings = {
        stopHSAerationTemperature: {
            key: agTypes.attributes.aeration.stopHSAerationTemperature,
            type: 'temperature'
        },
        startHSAerationTemperature: {
            key: agTypes.attributes.aeration.startHSAerationTemperature,
            type: 'temperature'
        },
        stopHSAerationHumidity: {
            key: agTypes.attributes.aeration.stopHSAerationHumidity,
            type: 'humidity'
        },
        startHSAerationHumidity: {
            key: agTypes.attributes.aeration.startHSAerationHumidity,
            type: 'humidity'
        },
        stopHSDewPointDifference: {
            key: agTypes.attributes.aeration.stopHSDewPointDifference,
            //label: 'Max difference between dewpoint and weather temperature',
            type: 'temperature'
        },
        startHSDewPointDifference: {
            key: agTypes.attributes.aeration.startHSDewPointDifference,
            //label: 'Max difference between dewpoint and weather temperature',
            type: 'temperature'
        },



        // grain settings
        startAerationTemperature: {
            key: agTypes.attributes.aeration.startAerationTemperature,
            type: 'temperature'
        },
        stopAerationTemperature: {
            key: agTypes.attributes.aeration.stopAerationTemperature,
            type: 'temperature'
        },
        startAerationDeltaTemperature: {
            key: agTypes.attributes.aeration.startAerationDeltaTemperature,
            type: 'temperature'
        },
        stopAerationDeltaTemperature: {
            key: agTypes.attributes.aeration.stopAerationDeltaTemperature,
            type: 'temperature'
        },

        // grain emc
        startAerationTopEmc: {
            key: agTypes.attributes.aeration.startAerationTopEmc,
            type: 'moisture'
        },
        stopAerationTopEmc: {
            key: agTypes.attributes.aeration.stopAerationTopEmc,
            type: 'moisture'
        },
        stopAerationBottomEmc: {
            key: agTypes.attributes.aeration.stopAerationBottomEmc,
            type: 'moisture'
        },
        startAerationBottomEmc: {
            key: agTypes.attributes.aeration.startAerationBottomEmc,
            type: 'moisture'
        },

        minGrainOutsideDifference: {
            key: agTypes.attributes.aeration.minGrainOutsideDifference,
            type: 'temperature',
            label: 'ag.aeration.min-grain-grain-outside-difference'
        },


        // weather station
        maxWeatherStationHumidity: {
            key: agTypes.attributes.aeration.maxWeatherStationHumidity,
            label: 'max weather humidity',
            type: 'humidity'
        },
        maxWeatherStationTemperature: {
            key: agTypes.attributes.aeration.maxWeatherStationTemperature,
            label: 'max weather temperature',
            type: 'temperature'
        },
        maxWeatherStationEmc: {
            key: agTypes.attributes.aeration.maxWeatherStationEmc,
            label: 'max weather moisture',
            type: 'moisture'
        },
        minWeatherStationEmc: {
            key: agTypes.attributes.aeration.minWeatherStationEmc,
            label: 'min weather moisture',
            type: 'moisture'
        },

        // other
        allowedTimeFrameFrom: {
            key: agTypes.attributes.aeration.allowedTimeFrameFrom,
            type: 'time'
        },
        allowedTimeFrameTo: {
            key: agTypes.attributes.aeration.allowedTimeFrameTo,
            type: 'time'
        }
    }

    vm.rules = {
        // headspace settings
        useHSTemperatureThresholds: {
            key: agTypes.attributes.aeration.useHSTemperatureThresholds,
            title: 'ag.aeration.use-temperature-thresholds',
            type: 'threshold',
            start: vm.settings.startHSAerationTemperature,
            stop: vm.settings.stopHSAerationTemperature
        },
        useHSHumidityThresholds: {
            key: agTypes.attributes.aeration.useHSHumidityThresholds,
            title: 'Use humidity threshold',
            type: 'threshold',
            start: vm.settings.startHSAerationHumidity,
            stop: vm.settings.stopHSAerationHumidity
        },
        useHSDewPointDifferenceThreshold: {
            key: agTypes.attributes.aeration.useHSDewPointDifference,
            title: 'Use difference between Dew Point and Outside Temperature threshold',
            type: 'threshold-reversed',
            start: vm.settings.startHSDewPointDifference,
            stop: vm.settings.stopHSDewPointDifference
        },

        // grain settings
        useTemperatureThresholds: {
            key: agTypes.attributes.aeration.useTemperatureThresholds,
            title: 'ag.aeration.use-temperature-thresholds',
            type: 'threshold',
            start: vm.settings.startAerationTemperature,
            stop: vm.settings.stopAerationTemperature
        },
        useDeltaTemperatureThresholds: {
            key: agTypes.attributes.aeration.useDeltaTemperatureThresholds,
            title: 'ag.aeration.use-delta-temperature-thresholds',
            type: 'threshold',
            start: vm.settings.startAerationDeltaTemperature,
            stop: vm.settings.stopAerationDeltaTemperature
        },
        useEmcThresholds: {
            key: agTypes.attributes.aeration.useEmcThresholds,
            title: 'Use EMC thresholds',
            type: 'bounded-threshold',
            startTop: vm.settings.startAerationTopEmc,
            stopTop: vm.settings.stopAerationTopEmc,
            stopBottom: vm.settings.stopAerationBottomEmc,
            startBottom: vm.settings.startAerationBottomEmc
        },
        useGrainOutsideTemperatureDifference: {
            key: agTypes.attributes.aeration.useGrainOutsideTemperatureDifference,
            title: 'ag.aeration.use-grain-outside-temperature-difference',
            type: 'constraints',
            settings: [
                vm.settings.minGrainOutsideDifference
            ]
        },


        // weather station settings
        useWeatherStationConstrain: {
            key: agTypes.attributes.aeration.useWeatherStationConstrain,
            title: 'ag.aeration.use-weather-station-constrain',
            type: 'constraints',
            settings: [
                vm.settings.maxWeatherStationHumidity,
                vm.settings.maxWeatherStationTemperature
            ]
        },
        useWeatherStationEmcConstrain: {
            key: agTypes.attributes.aeration.useWeatherStationEmcConstrain,
            title: 'Use weather station EMC constraint',
            type: 'constraints',
            settings: [
                vm.settings.maxWeatherStationEmc,
                vm.settings.minWeatherStationEmc
            ]
        },

        // general
        useTimeFrameConstrain: {
            key: agTypes.attributes.aeration.useTimeFrameConstrain,
            title: 'ag.aeration.use-time-frame-constrain',
            type: 'timeframe',
            start: vm.settings.allowedTimeFrameFrom,
            stop: vm.settings.allowedTimeFrameTo
        }
    }
    vm.sections = [
        {
            title: 'Head-space settings',
            supportedBy: [agTypes.aerationType.roof],
            rules: [
                vm.rules.useHSTemperatureThresholds,
                vm.rules.useHSHumidityThresholds,
                vm.rules.useHSDewPointDifferenceThreshold
            ]
        },
        {
            title: 'ag.aeration.temperature-settings',
            supportedBy: [agTypes.aerationType.alarm],
            rules: [
                vm.rules.useTemperatureThresholds
            ]
        },
        {
            title: 'ag.aeration.temperature-settings',
            supportedBy: [agTypes.aerationType.aeration],
            rules: [
                vm.rules.useTemperatureThresholds,
                vm.rules.useDeltaTemperatureThresholds,
                vm.rules.useEmcThresholds,
                vm.rules.useGrainOutsideTemperatureDifference
            ]
        },
        {
            title: 'ag.aeration.weather-station-settings',
            supportedBy: [agTypes.aerationType.roof],
            rules: [
                vm.rules.useWeatherStationConstrain
            ]
        },
        {
            title: 'ag.aeration.weather-station-settings',
            supportedBy: [agTypes.aerationType.aeration],
            rules: [
                vm.rules.useWeatherStationConstrain,
                vm.rules.useWeatherStationEmcConstrain
            ]
        }
    ]

    for (let ruleKey in vm.rules) {
        let rule = vm.rules[ruleKey];
        if (!rule.key) {
            $log.warn('invalid aeration key: ', ruleKey);
        }
    }
    for (let settingKey in vm.settings) {
        let setting = vm.settings[settingKey];
        if (!setting.key) {
            $log.warn('invalid aeration setting key:', settingKey);
        }
    }

    $scope.$watch('vm.ctx', function() {
        if (vm.ctx) {
            vm.ctx.widgetActions = [];
            vm.ctx.hideTitlePanel = false;

            vm.aerationId = null;
            if (vm.ctx.stateController) {
                vm.aerationId = vm.ctx.stateController.getStateParams().entityId;
            }
            loadAeration();
            $scope.$watch(function() {
                    return vm.ctx.stateController.getStateParams().entityId;
                },
                function (newAerationId, prevAerationId) {
                    if (newAerationId && !angular.equals(newAerationId, prevAerationId)) {
                        vm.aerationId = newAerationId;
                        loadAeration();
                    }
                }
            );

        }
    });

    $scope.$watch(function() { return vm.aeration ? (vm.aeration.label || vm.aeration.name) : null; }, function(title) {
        if (vm.showData && vm.ctx && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.aeration.aeration-settings') + ': ' + title;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    $scope.$watch("vm.aeration.autoControl", (newVal) => {
        if (newVal) {
            vm.aeration.manualControl = false;
        }
    });

    $scope.$watch('vm.allowedTimeFrameFrom + vm.allowedTimeFrameTo', () => {
        if (vm.aeration && !$scope.theForm.$invalid) {
            var from = getMillisOfDay(vm.allowedTimeFrameFrom);
            var to = getMillisOfDay(vm.allowedTimeFrameTo);
            vm.aeration.allowedTimeFrameFrom = from;
            vm.aeration.allowedTimeFrameTo = to;
        }
    });

    function loadAeration() {
        vm.aeration = null;
        vm.origAeration = null;
        reset();
        if (vm.aerationId) {
            vm.loading = true;
            aerationService.getAeration(vm.aerationId).then(
                function success(aeration) {
                    $log.info('aeration:', {...aeration});
                    vm.origAeration = aeration;
                    vm.aeration = angular.copy(aeration);
                    updateTimeFrame(vm.aeration.allowedTimeFrameFrom, vm.aeration.allowedTimeFrameTo);
                    aerationTypeChange()
                    reloadAerationRelays();
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    }

    function reloadAerationRelays() {
        if (vm.aerationId) {
            aerationService.getAerationRelays(vm.aerationId).then(
                function success(relays) {
                    if (vm.aeration) {
                        vm.relays = $filter('orderBy')(relays, 'name');
                    }
                    vm.loading = false;
                    resetFormState();
                },
                function fail() {
                    vm.relays = [];
                    vm.loading = false;
                    resetFormState();
                }
            );
        }
    }

    function save($event) {
        var title = $translate.instant('ag.aeration.confirm-settings-title');
        var content = $translate.instant('ag.aeration.confirm-settings-text');
        var confirm = $mdDialog.confirm()
            .targetEvent($event)
            .title(title)
            .htmlContent(content)
            .ariaLabel(title)
            .cancel($translate.instant('action.no'))
            .ok($translate.instant('action.yes'));
        $mdDialog.show(confirm).then(
            () => {
                if (vm.aeration) {
                    var attributes = [];
                    var aerationSettingsTasks = [];
                    for (var a in agTypes.attributes.aeration) {
                        var attr = agTypes.attributes.aeration[a];
                        attributes.push({key: attr, value: vm.aeration[attr]});
                    }
                    $log.info("save aeration attributes", attributes)
                    aerationSettingsTasks.push(attributeService.saveEntityAttributes(vm.aeration.id.entityType, vm.aeration.id.id, types.attributesScope.server.value, attributes));
                    if (vm.aeration.label !== vm.origAeration.label) {
                        aerationSettingsTasks.push(assetService.saveAsset(vm.aeration));
                    }
                    $q.all(aerationSettingsTasks).then(() => {
                        vm.origAeration = angular.copy(vm.aeration);
                        $scope.theForm.$setPristine();
                        $scope.theForm.$setUntouched();
                        $rootScope.$broadcast('siteAssetUpdated', vm.aeration);
                    });
                }
            }
        );
    }

    function cancel() {
        vm.aeration = angular.copy(vm.origAeration);
        updateTimeFrame(vm.aeration.allowedTimeFrameFrom, vm.aeration.allowedTimeFrameTo);
        resetFormState();
    }

    function getMillisOfDay(date) {
        if (date) {
            var minutes = (date.getUTCHours() * 60) + date.getUTCMinutes()
            return minutes * 60 * 1000
        } else {
            return 0;
        }
    }

    function updateTimeFrame(from, to) {
        var now = new Date();
        var startTime = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0,0,0);
        var endTime = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            23,59,59);
        var timezone = new Date().getTimezoneOffset() * 60000
        if (angular.isDefined(from)) {
            vm.allowedTimeFrameFrom = new Date(startTime.getTime() + from - timezone);
        } else {
            vm.allowedTimeFrameFrom = startTime;
        }
        if (angular.isDefined(to)) {
            vm.allowedTimeFrameTo = new Date(startTime.getTime() + to - timezone);
        } else {
            vm.allowedTimeFrameTo = endTime;
        }
        
    }

    function reset() {
        vm.allowedTimeFrameFrom = null;
        vm.allowedTimeFrameTo = null;
    }

    function resetFormState() {
        $timeout(() => {
            if ($scope.theForm.updateValidity) {
                $scope.theForm.updateValidity();
            }
            $scope.theForm.$setPristine();
            $scope.theForm.$setUntouched();
        });
    }

    function clearUnsupportedRules () {
        let supportedRules = {};
        for (let section of vm.sections) {
            let isSupported = section.supportedBy.includes(vm.aeration.aerationType);
            for (let rule of section.rules) {
                if (!supportedRules[rule.key]) {
                    supportedRules[rule.key] = isSupported;
                }
            }
        }
        $log.info('supported rules', supportedRules);
        for (let rule in supportedRules) {
            if (!supportedRules[rule]) {
                $log.info('auto disable rule', rule, 'not supported by', vm.aeration.aerationType)
                vm.aeration[rule.key] = false;
            }
        }
    }

    function aerationTypeChange() {
        if (vm.aeration) {
            clearUnsupportedRules()
            var isAlarm = vm.aeration.aerationType === agTypes.aerationType.alarm;
            if (isAlarm) {
                vm.isAutoControlSupported = false;
                vm.autoControl = true;    
            } else {
                vm.isAutoControlSupported = true
            }
            //vm.aeration.autoControl = (vm.aeration.aerationType === agTypes.aerationType.alarm);
            //vm.aeration.useTemperatureThresholds = (vm.aeration.aerationType === agTypes.aerationType.alarm);
        }
    }
}